import { For, Show, createMemo, createSignal } from "solid-js";

import { SelectionActionBar } from "~/components/SelectionActionBar";
import { TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import {
  useArchiveDeal,
  useMoveDealToStage,
  useStages,
  useUpdateDeal,
} from "~/lib/hooks";
import type { DealListItem } from "~/lib/types";

type Props = {
  wsKey: () => string;
  // The pipeline currently in view — used for the stage-move action so it
  // doesn't move deals into a stage from a different pipeline.
  pipelineId: () => string | undefined;
  count: () => number;
  rows: () => DealListItem[];
  onClear: () => void;
};

type Mode = "archive" | "tags" | "owner" | "stage";
type RowOutcome = { id: string; name: string; ok: boolean; message?: string };

export function DealsBulkBar(props: Props) {
  const archiveMutation = useArchiveDeal(props.wsKey);
  const updateMutation = useUpdateDeal(props.wsKey);
  const moveMutation = useMoveDealToStage(props.wsKey);
  const stages = useStages(props.wsKey, props.pipelineId);

  const [mode, setMode] = createSignal<Mode | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [tagsDraft, setTagsDraft] = createSignal<string[]>([]);
  const [ownerDraft, setOwnerDraft] = createSignal<string | null>(null);
  const [ownerNameDraft, setOwnerNameDraft] = createSignal<string | null>(null);
  const [stageDraft, setStageDraft] = createSignal<string>("");
  const [outcome, setOutcome] = createSignal<RowOutcome[] | null>(null);

  const reset = () => {
    setMode(null);
    setTagsDraft([]);
    setOwnerDraft(null);
    setOwnerNameDraft(null);
    setStageDraft("");
    setOutcome(null);
  };

  const close = () => reset();

  const dedupeTags = (a: string[], b: string[]): string[] => {
    const seen = new Map<string, string>();
    for (const t of [...a, ...b]) {
      const k = t.trim().toLowerCase();
      if (k && !seen.has(k)) seen.set(k, t.trim());
    }
    return Array.from(seen.values());
  };

  // Only include rows that match the visible pipeline when moving stages —
  // moving a deal into a foreign pipeline's stage would break the model.
  const movableRows = createMemo(() =>
    props.rows().filter((r) => !props.pipelineId() || r.pipeline_id === props.pipelineId()),
  );

  const runArchive = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        await archiveMutation.mutateAsync({ dealId: r.id, version: r.version });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id, name: r.name, ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runAddTags = async () => {
    if (tagsDraft().length === 0) return;
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        const merged = dedupeTags(r.tags ?? [], tagsDraft());
        await updateMutation.mutateAsync({
          dealId: r.id, version: r.version,
          body: { tags: merged },
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id, name: r.name, ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runChangeOwner = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        await updateMutation.mutateAsync({
          dealId: r.id, version: r.version,
          body: { owner_user_id: ownerDraft() ?? null },
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id, name: r.name, ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runMoveStage = async () => {
    if (!stageDraft()) return;
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of movableRows()) {
      try {
        await moveMutation.mutateAsync({
          dealId: r.id, version: r.version, stage_id: stageDraft(),
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id, name: r.name, ok: false,
          message: (err as Error).message,
        });
      }
    }
    // Surface skipped rows (foreign pipeline) so the user understands why
    // count < selected.
    for (const r of props.rows()) {
      if (!movableRows().includes(r)) {
        results.push({
          id: r.id, name: r.name, ok: false,
          message: "Skipped: deal belongs to a different pipeline.",
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const okCount = () => outcome()?.filter((r) => r.ok).length ?? 0;
  const errCount = () => outcome()?.filter((r) => !r.ok).length ?? 0;

  return (
    <>
      <SelectionActionBar count={props.count} onClear={props.onClear}>
        <button
          type="button"
          class="btn btn-sm btn-warning"
          onClick={() => setMode("archive")}
        >
          Archive
        </button>
        <button type="button" class="btn btn-sm" onClick={() => setMode("tags")}>
          Add tags
        </button>
        <button type="button" class="btn btn-sm" onClick={() => setMode("owner")}>
          Change owner
        </button>
        <button
          type="button"
          class="btn btn-sm"
          disabled={!props.pipelineId()}
          title={
            props.pipelineId() ? "" : "Switch to a single pipeline to bulk-move stages"
          }
          onClick={() => setMode("stage")}
        >
          Move stage
        </button>
      </SelectionActionBar>

      <Show when={mode()}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="bulk-deal-title"
        >
          <div class="modal-box max-w-xl">
            <h3 id="bulk-deal-title" class="font-bold text-lg mb-3">
              <Show when={mode() === "archive"}>
                Archive {props.count()} deal{props.count() === 1 ? "" : "s"}?
              </Show>
              <Show when={mode() === "tags"}>Add tags to {props.count()} deals</Show>
              <Show when={mode() === "owner"}>Change owner on {props.count()} deals</Show>
              <Show when={mode() === "stage"}>
                Move {movableRows().length} of {props.count()} deals to a new stage
              </Show>
            </h3>

            <Show when={!outcome()}>
              <Show when={mode() === "archive"}>
                <p class="text-sm opacity-80 mb-4">
                  Archived deals are hidden from lists. This can be undone
                  per-deal from each detail page.
                </p>
              </Show>

              <Show when={mode() === "tags"}>
                <p class="text-sm opacity-80 mb-3">
                  Tags will be added to the existing set on each deal.
                </p>
                <TagsEditor
                  wsKey={props.wsKey}
                  entityType="deal"
                  value={tagsDraft}
                  onChange={setTagsDraft}
                />
              </Show>

              <Show when={mode() === "owner"}>
                <p class="text-sm opacity-80 mb-3">
                  Pick a new owner (or clear the chip to unassign).
                </p>
                <UserPicker
                  wsKey={props.wsKey}
                  value={ownerDraft}
                  selectedName={ownerNameDraft}
                  onChange={(id, name) => {
                    setOwnerDraft(id);
                    setOwnerNameDraft(name);
                  }}
                />
              </Show>

              <Show when={mode() === "stage"}>
                <Show
                  when={movableRows().length === props.count()}
                  fallback={
                    <p class="text-sm text-warning mb-3">
                      {props.count() - movableRows().length} selected deal
                      {props.count() - movableRows().length === 1 ? " is" : "s are"} in
                      a different pipeline and will be skipped.
                    </p>
                  }
                >
                  <p class="text-sm opacity-80 mb-3">Pick the destination stage.</p>
                </Show>
                <select
                  class="select select-bordered w-full"
                  aria-label="Destination stage"
                  value={stageDraft()}
                  onChange={(e) => setStageDraft(e.currentTarget.value)}
                >
                  <option value="">— pick a stage —</option>
                  <For each={stages.data ?? []}>
                    {(s) => (
                      <option value={s.id}>
                        {s.name} {s.stage_type === "won" ? "(won)" : s.stage_type === "lost" ? "(lost)" : ""}
                      </option>
                    )}
                  </For>
                </select>
              </Show>
            </Show>

            <Show when={outcome()}>
              {(rows) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{rows().length}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Succeeded</div>
                      <div class="stat-value text-lg text-success">{okCount()}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Failed</div>
                      <div class="stat-value text-lg text-error">{errCount()}</div>
                    </div>
                  </div>
                  <Show when={errCount() > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Failed rows"
                    >
                      <ul class="text-xs space-y-1">
                        <For each={rows().filter((r) => !r.ok)}>
                          {(r) => (
                            <li>
                              <strong>{r.name}</strong>: {r.message ?? "Failed"}
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button
                type="button"
                class="btn btn-ghost"
                onClick={close}
                disabled={busy()}
              >
                {outcome() ? "Close" : "Cancel"}
              </button>
              <Show when={!outcome()}>
                <Show when={mode() === "archive"}>
                  <button
                    type="button"
                    class="btn btn-warning"
                    onClick={runArchive}
                    disabled={busy()}
                  >
                    {busy() ? "Archiving…" : "Archive"}
                  </button>
                </Show>
                <Show when={mode() === "tags"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runAddTags}
                    disabled={busy() || tagsDraft().length === 0}
                  >
                    {busy() ? "Tagging…" : "Add tags"}
                  </button>
                </Show>
                <Show when={mode() === "owner"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runChangeOwner}
                    disabled={busy()}
                  >
                    {busy() ? "Updating…" : "Apply"}
                  </button>
                </Show>
                <Show when={mode() === "stage"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runMoveStage}
                    disabled={busy() || !stageDraft() || movableRows().length === 0}
                  >
                    {busy() ? "Moving…" : "Move"}
                  </button>
                </Show>
              </Show>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
